import { create } from '@bufbuild/protobuf'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type {
  Supply48DataDay,
  Supply48DataSource,
} from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import {
  SaveSupply48DataRowSchema,
  Supply48SlotValueSchema,
} from '@ebalance/proto/bffuser/v1/supply_48_data_model_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import type {
  EditRowKey,
  EditRowState,
  EditStateMap,
  UpdatedAtOverlay,
} from '~/pages/Supply48Data/models/editState'
import { toDateKey } from '~/pages/Supply48Data/models/gridRows'
import { isEditableSource } from '~/pages/Supply48Data/models/sourceKind'

/**
 * dayIndex: page で 1 回だけ useMemo 構築する rowKey → Supply48DataDay のマップ。
 * legId の存在チェックに使う legIds とは別に dayIndex を参照すると updatedAt を取得できる。
 */
export type DayIndex = Map<EditRowKey, Supply48DataDay>

/**
 * 保存対象になり得る source id 集合を data.sources から求める。編集可能な
 * source (`isEditableSource` の allow-list、現状 CONTRACT のみ) だけを含み、
 * 市場等の非編集 source (AC-S4D-032: 保存対象外) は除外する。
 *
 * 市場セルはグリッド側で常時 read-only (`cellAt` の `editable` gate) なので
 * dirty 化する経路は無いが、buildSaveRows は「legIds に無い source の dirty 行
 * = stale (画面から消えた行)」として扱う既存の仕組みを持つ。呼び出し側
 * (useSupply48EditSession) がこの集合を legIds として渡すことで、万一 dirty 化
 * しても市場 source id は SaveSupply48Data の payload に一件も載らない
 * (多層防御 — grid 側の read-only gate 1 箇所だけに依存しない)。
 *
 * 注意: 「載らない」の実現手段は stale 経路の再利用であり、当該行だけを黙って
 * 落として残りを保存する動きではない — buildSaveRows の hasStale=true は
 * useSupply48EditSession.doSave を早期 return させ、保存リクエスト全体を送信
 * しない (stale なのはこの source の 1 行だけでも、他の正当な dirty 行も道連れで
 * 保存されない)。実運用では市場セルが dirty 化する経路自体が無いためこの分岐は
 * 到達しない想定 (= 多層防御の最終防御線)。
 */
export function editableSourceIds(sources: Supply48DataSource[]): Set<string> {
  return new Set(
    sources.filter((s) => isEditableSource(s)).map((s) => s.supply48SourceId),
  )
}

/**
 * buildSaveRows の戻り値。
 */
// SaveRow 型エイリアスは buildSaveRows の戻り型を単純化するために定義する
export type SaveRow = ReturnType<
  typeof create<typeof SaveSupply48DataRowSchema>
>
type SlotRow = ReturnType<typeof create<typeof Supply48SlotValueSchema>>

export type BuildSaveRowsResult = {
  rows: SaveRow[]
  hasStale: boolean
}

/**
 * §3 保存の純関数化 + バグA修正。
 *
 * dirty 行を走査し保存 payload を構築する。
 *
 * 判定規則:
 * - leg が legIds に存在しない → stale (leg 消失)
 * - dayData なし && 日付が visibleDates (表示期間) 内 → 新規行 (expectedUpdatedAt=undefined)
 * - dayData なし && 期間外 → stale (バグA: 旧実装では全体中止だったが、本実装では新規行を通す)
 *
 * @param editState 現在の編集状態
 * @param dayIndex rowKey → Supply48DataDay のマップ (page で useMemo 構築)
 * @param legIds 現在表示されている legId の Set
 * @param visibleDates 表示期間の ProtoDate 一覧 (編集可能な日付ドメイン)
 * @param overlay 保存成功後の updatedAt オーバーレイ
 */
export function buildSaveRows(
  editState: EditStateMap,
  dayIndex: DayIndex,
  legIds: Set<string>,
  visibleDates: ProtoDate[],
  overlay: UpdatedAtOverlay,
): BuildSaveRowsResult {
  const dirty = Array.from(editState.entries()).filter(
    ([, state]) => state.dirty,
  )
  let hasStale = false
  const rows: BuildSaveRowsResult['rows'] = []

  // visibleDates を Set にして O(1) 判定 (toDateKey で統一)
  const visibleDateSet = new Set<string>(visibleDates.map(toDateKey))

  for (const [rowKey, rowState] of dirty) {
    const { legId: sourceId, deliveryDate } = rowState
    const { year, month, day } = deliveryDate

    // source 消失チェック (rowState.legId は行キー = supply_48_source_id を保持する
    // 既存 field 名。supply-48-source-redesign で意味は leg → source に変わったが、
    // grid 内部の行キー呼称としては legId のまま維持する — 機械的追随の対象は
    // proto field 名のみ)。
    if (!legIds.has(sourceId)) {
      hasStale = true
      continue
    }

    const dayData = dayIndex.get(rowKey)
    const dateStr = toDateKey(deliveryDate as ProtoDate)

    if (dayData === undefined) {
      // dayData なし
      if (!visibleDateSet.has(dateStr)) {
        // 期間外 → stale
        hasStale = true
        continue
      }
      // 表示期間内 → 新規行 (バグA修正: Create 経路)
      const slots = buildSlots(rowState, undefined)
      rows.push(
        create(SaveSupply48DataRowSchema, {
          supply48SourceId: sourceId,
          deliveryDate: { year, month, day },
          slots,
          manualEditKomas: Array.from(rowState.manualEditKomas),
          expectedUpdatedAt: undefined,
        }),
      )
    } else {
      // 既存行: 編集値を元値にマージ
      const effectiveUpdatedAt: Timestamp | undefined = overlay.has(rowKey)
        ? overlay.get(rowKey)
        : dayData.updatedAt
      const slots = buildSlots(rowState, dayData)
      rows.push(
        create(SaveSupply48DataRowSchema, {
          supply48SourceId: sourceId,
          deliveryDate: { year, month, day },
          slots,
          manualEditKomas: Array.from(rowState.manualEditKomas),
          expectedUpdatedAt: effectiveUpdatedAt,
        }),
      )
    }
  }

  return { rows, hasStale }
}

/**
 * 48 コマのスロット値を構築する。
 * 編集値があれば上書き、なければ元の値 (dayData) をそのまま使う。
 * dayData が undefined (新規行) の場合は編集値のみ。未編集コマは unset (value=undefined)。
 */
function buildSlots(
  rowState: EditRowState,
  dayData: Supply48DataDay | undefined,
): SlotRow[] {
  return Array.from({ length: 48 }, (_, i) => {
    const editedValue = rowState.slots[i]
    if (editedValue !== undefined) {
      return create(Supply48SlotValueSchema, {
        value: editedValue !== null ? editedValue : undefined,
      })
    }
    if (dayData !== undefined) {
      // 既存行: 元の値を使う
      const original = dayData.slots[i]?.value
      return create(Supply48SlotValueSchema, {
        value: original !== undefined ? original : undefined,
      })
    }
    // 新規行: 未編集コマは unset
    return create(Supply48SlotValueSchema, { value: undefined })
  })
}
